import { useSelector, useDispatch } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { 
  Home, 
  AlertTriangle, 
  Wrench, 
  Settings, 
  BarChart3, 
  Users,
  Building,
  ChevronRight
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

import { selectSidebarOpen, toggleSidebar } from '@/store/slices/uiSlice'
import { selectUser } from '@/store/slices/authSlice'
import { useGetAlertsQuery, useGetWorkOrdersQuery } from '@/store/api'

interface NavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  badge?: number
  roles?: string[]
}

export default function Sidebar() {
  const dispatch = useDispatch()
  const sidebarOpen = useSelector(selectSidebarOpen)
  const user = useSelector(selectUser)
  
  // Fetch real-time counts from API
  const { data: alertsData } = useGetAlertsQuery({ 
    limit: 100, // Get enough to count unread
    sortBy: 'created_at', // Use snake_case for database column
    sortOrder: 'desc' 
  })
  
  const { data: workOrdersData } = useGetWorkOrdersQuery({ 
    limit: 100, // Get enough to count overdue
    status: ['pending', 'assigned', 'in-progress'], // Use hyphen format (backend normalizes)
    sortBy: 'created_at', // Use snake_case for database column
    sortOrder: 'desc' 
  })
  
  // Calculate real counts
  const alerts = alertsData?.data || []
  const workOrders = workOrdersData?.data || []
  
  // Count unread/critical alerts
  const alertCount = alerts.filter(alert => 
    alert.status === 'new' || alert.priority === 'critical'
  ).length
  
  // Count active work orders (pending, assigned, in-progress)
  const workOrderCount = workOrders.filter(wo => {
    // Count all active work orders
    return wo.status === 'pending' || wo.status === 'assigned' || wo.status === 'in-progress'
  }).length

  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: Home,
    },
    {
      path: '/alerts',
      label: 'Alerts',
      icon: AlertTriangle,
      badge: alertCount,
    },
    {
      path: '/work-orders',
      label: 'Work Orders',
      icon: Wrench,
      badge: workOrderCount,
    },
    {
      path: '/equipment',
      label: 'Equipment',
      icon: Settings,
    },
    {
      path: '/properties',
      label: 'Properties',
      icon: Building,
    },
    {
      path: '/analytics',
      label: 'Analytics',
      icon: BarChart3,
      roles: ['property_manager', 'owner'],
    },
  ]

  const adminItems: NavItem[] = [
    {
      path: '/users',
      label: 'Users',
      icon: Users,
      roles: ['owner', 'property_manager'],
    },
  ]

  const isItemVisible = (item: NavItem) => {
    if (!item.roles) return true
    return item.roles.includes(user?.role || '')
  }

  const NavItemComponent = ({ item }: { item: NavItem }) => {
    const Icon = item.icon
    
    return (
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          `flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors group ${
            isActive
              ? 'bg-trust-blue text-white'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          }`
        }
      >
        <Icon className={`flex-shrink-0 w-5 h-5 ${sidebarOpen ? 'mr-3' : ''}`} />
        {sidebarOpen && (
          <>
            <span className="truncate">{item.label}</span>
            {item.badge && item.badge > 0 && (
              <Badge 
                className="ml-auto bg-critical-red text-white"
                variant="destructive"
              >
                {item.badge > 9 ? '9+' : item.badge}
              </Badge>
            )}
          </>
        )}
        {!sidebarOpen && item.badge && item.badge > 0 && (
          <Badge 
            className="absolute left-8 top-1 bg-critical-red text-white h-4 w-4 rounded-full p-0 text-xs"
            variant="destructive"
          >
            {item.badge > 9 ? '9+' : item.badge}
          </Badge>
        )}
      </NavLink>
    )
  }

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden" 
          onClick={() => dispatch(toggleSidebar())}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        } ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center px-4 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-trust-blue rounded-lg flex items-center justify-center text-white font-bold text-sm">
              P
            </div>
            {sidebarOpen && (
              <div className="ml-3">
                <div className="text-lg font-bold text-gray-900">
                  Predictive<span className="text-trust-blue">MX</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {/* Main navigation */}
          <div className="space-y-1">
            {navItems.filter(isItemVisible).map((item) => (
              <NavItemComponent key={item.path} item={item} />
            ))}
          </div>

          {/* Admin section */}
          {adminItems.filter(isItemVisible).length > 0 && (
            <>
              <Separator className="my-4" />
              {sidebarOpen && (
                <div className="px-3 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Administration
                  </h3>
                </div>
              )}
              <div className="space-y-1">
                {adminItems.filter(isItemVisible).map((item) => (
                  <NavItemComponent key={item.path} item={item} />
                ))}
              </div>
            </>
          )}
        </nav>

        {/* Collapse button */}
        <div className="p-3 border-t border-gray-200">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center lg:justify-start"
            onClick={() => dispatch(toggleSidebar())}
          >
            <ChevronRight 
              className={`w-4 h-4 transition-transform ${
                sidebarOpen ? 'rotate-180' : ''
              }`} 
            />
            {sidebarOpen && <span className="ml-2">Collapse</span>}
          </Button>
        </div>
      </div>
    </>
  )
}